import React, { useEffect, useState } from 'react';
import { BsPass } from 'react-icons/bs';
import { FaSkating } from 'react-icons/fa';
import { MdCelebration, MdFilePresent, MdSportsHockey } from 'react-icons/md';
import { TiTicket } from 'react-icons/ti';
import { SeasonStatistics } from '../../@types';
import { AdminService } from '../../services';

interface SeasonStatisticsBannerProps {
  userRole?: string;
}

export const SeasonStatisticsBanner: React.FC<SeasonStatisticsBannerProps> = ({
  userRole,
}) => {
  const [statistics, setStatistics] = useState<SeasonStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to check if user has moderator access
  const hasModeratorAccess = () => {
    if (!userRole) return false;
    return ['admin', 'super_admin'].includes(userRole);
  };

  // Fetch statistics on mount if user has moderator access
  useEffect(() => {
    const fetchStatistics = async () => {
      if (hasModeratorAccess()) {
        try {
          setIsLoading(true);
          const responseData =
            (await AdminService.getAdminStatistics()) as unknown as SeasonStatistics;
          setStatistics(responseData);
          setError(null);
        } catch (error) {
          setError('Failed to load statistics');
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    // Fetch immediately on mount
    fetchStatistics();

    // Set up auto-refresh every 5 minutes (300000ms)
    const refreshInterval = setInterval(() => {
      if (hasModeratorAccess()) {
        fetchStatistics();
      }
    }, 300000); // 5 minutes

    // Cleanup interval on unmount
    return () => {
      clearInterval(refreshInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // Don't render if user doesn't have access
  if (!hasModeratorAccess()) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-center">
            <span className="text-sm text-gray-600">Loading statistics...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !statistics) {
    return (
      <div className="bg-red-50 border-b border-red-100">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-center">
            <span className="text-sm text-red-600">
              {error || 'No statistics available'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border-b border-blue-100">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-around flex-wrap gap-2">
          {/* Tickets */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md shadow-sm">
            <TiTicket size={18} className="text-blue-600" />
            <span className="text-xl font-semibold text-gray-800">
              {statistics.skatingBookingsCount}
            </span>
            <span className="text-xs text-gray-500">Tickets</span>
          </div>

          {/* Skating Lessons */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md shadow-sm">
            <FaSkating size={18} className="text-blue-600" />
            <span className="text-xl font-semibold text-gray-800">
              {statistics.skatingLessonsCount}
            </span>
            <span className="text-xs text-gray-500">Lessons</span>
          </div>

          {/* Hockey Lessons */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md shadow-sm">
            <MdSportsHockey size={18} className="text-blue-600" />
            <span className="text-xl font-semibold text-gray-800">
              {statistics.hockeyLessonsCount}
            </span>
            <span className="text-xs text-gray-500">Hockey</span>
          </div>

          {/* Skating Passes */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md shadow-sm">
            <BsPass size={18} className="text-blue-600" />
            <span className="text-xl font-semibold text-gray-800">
              {statistics.skatingPassesCount}
            </span>
            <span className="text-xs text-gray-500">Passes</span>
          </div>

          {/* Parties */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md shadow-sm">
            <MdCelebration size={18} className="text-blue-600" />
            <span className="text-xl font-semibold text-gray-800">
              {statistics.partyBookingsCount}
            </span>
            <span className="text-xs text-gray-500">Parties</span>
          </div>

          {/* Waivers */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md shadow-sm">
            <MdFilePresent size={18} className="text-blue-600" />
            <span className="text-xl font-semibold text-gray-800">
              {statistics.customerWaiversCount}
            </span>
            <span className="text-xs text-gray-500">Waivers</span>
          </div>
        </div>
      </div>
    </div>
  );
};
